import { createAxiosInstance } from "./axios";

/**
 * Service for Bearcat API calls
 * This service handles both authenticated and unauthenticated requests
 */

// Create instance for Bearcat API without token (for unauthenticated endpoints)
const bearcatApiWithoutToken = createAxiosInstance(
  import.meta.env.VITE_BEARCAT_API, 
  { withToken: false }
);

// Create instance for Bearcat API with token (for authenticated endpoints)
const bearcatApiWithToken = createAxiosInstance(
  import.meta.env.VITE_BEARCAT_API, 
  { withToken: true }
);

/**
 * Get auction URLs configuration from the server
 * This endpoint does not require authentication
 *
 * Expected API response format:
 * {
 *   "myAccountURL": "https://auction.example.com/myaccount",
 *   "registerURL": "https://auction.example.com/register",
 *   "inventoryURL": "https://auction.example.com/search"
 * }
 *
 * @returns {Promise<Object>} Object containing various auction URLs
 */
export const getAuctionUrls = async () => {
  try {
    const { data } = await bearcatApiWithoutToken.get("/Auction/GetAuctionURLs");
    return data;
  } catch (error) {
    console.error("Error fetching auction URLs:", error);
    throw error;
  }
};

/**
 * Get upcoming events/auctions
 * This endpoint requires authentication
 *
 * @param {Object} options - Request options
 * @param {string} options.vSortFilter - Sort filter (default: "dateasc")
 * @param {number} options.pageNumber - Page number (default: 1)
 * @param {AbortSignal} options.signal - Optional abort signal for cancellation
 * @returns {Promise<Array>} Array of event objects
 */
export const getUpcomingEvents = async (options = {}) => {
  try {
    const { vSortFilter = "dateasc", pageNumber = 1, signal } = options;

    const { data } = await bearcatApiWithToken.request({
      signal,
      method: "post",
      url: "/Auction/UpcomingEvents",
      data: {
        vSortFilter,
        pageNumber,
      },
    });

    return data.result ? data.result : [];
  } catch (error) {
    console.error("Error fetching upcoming events:", error);
    throw error;
  }
};

/**
 * Get terms and conditions for an event
 * This endpoint requires authentication
 *
 * @param {string|number} eventId - Event ID
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<Object>} Terms and conditions
 */
export const getTermsAndConditions = async (eventId, signal) => {
  try {
    const { data } = await bearcatApiWithToken.get(`/Event/GetEventTermsAndConditions?eventId=${eventId}`, {
      signal,
    });
    return data.result;
  } catch (error) {
    console.error("Error fetching terms and conditions:", error);
    throw error;
  }
};

/**
 * User login
 * This endpoint does not require authentication
 *
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {Object} impersonate - Optional impersonate user object
 * @returns {Promise<Object>} Login response
 */
export const login = async (username, password, impersonate) => {
  try {
    const { data } = await bearcatApiWithoutToken.post("/Auth/login", {
      user_Name: username,
      password: password,
      grant_Type: "password",
      route_ID: 1,
      impersonate_ID: impersonate?.id,
    });
    return data;
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
};

/**
 * Refresh authentication token
 * This endpoint does not require authentication
 *
 * @param {string} refreshToken - Refresh token
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Refresh response
 */
export const refresh = async (refreshToken, userId) => {
  try {
    const { data } = await bearcatApiWithoutToken.post("/Auth/refresh", {
      token: refreshToken,
      impersonate_ID: userId,
    });
    return data;
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

/**
 * Handoff authentication token
 * This endpoint does not require authentication
 *
 * @param {string} handoffToken - Handoff token
 * @returns {Promise<Object>} Handoff response
 */
export const handoff = async (handoffToken) => {
  try {
    const { data } = await bearcatApiWithoutToken.post("/Auth/handoff", {
      token: handoffToken,
    });
    return data;
  } catch (error) {
    console.error("Error during handoff:", error);
    throw error;
  }
};

/**
 * Create a Cloudflare Stream live input via Bearcat API proxy
 * Credentials are kept server-side — this replaces direct Cloudflare API calls from the browser.
 *
 * @param {number|string} eventId - Event ID used for the meta name
 * @returns {Promise<Object>} Response with { result: { uid, webRTC: { url }, webRTCPlayback: { url } } }
 */
export const createCloudflareLiveInput = async (eventId) => {
  try {
    const { data } = await bearcatApiWithToken.post("/cloudflare/stream/live-input", {
      eventId: Number(eventId),
    });
    return data;
  } catch (error) {
    console.error("Error creating Cloudflare live input:", error);
    throw error;
  }
};

/**
 * Delete a Cloudflare Stream live input via Bearcat API proxy
 *
 * @param {string} liveInputId - The uid of the live input to delete
 * @returns {Promise<Object>} Response with { success: true }
 */
export const deleteCloudflareLiveInput = async (liveInputId) => {
  try {
    const { data } = await bearcatApiWithToken.delete(`/cloudflare/stream/live-input/${liveInputId}`);
    return data;
  } catch (error) {
    console.error("Error deleting Cloudflare live input:", error);
    throw error;
  }
};
